import React, { useEffect, useState } from 'react'
import { useNavigation, useRoute } from '@react-navigation/native'
import { View } from 'react-native'
import { GetIcon } from '../../../../ui/atoms/GetIcon'
import { paths } from '../../../links'
import {
  IconArrowRightLightly,
  NewAttchmentIcon,
} from '../../../../assets/icons'
import { useTranslation } from 'react-i18next'
import { isWeb } from '../../../../utils/helpers/commonUtils'
import cookies from '../../../../utils/service/cooker'
import RootStore from '../../../../store/RootStore'
import Wrapper from '../../Wrapper'
import { BtButton, BtCheckBox, BtText } from '@linqpal/components/src/ui'
import { TouchableOpacity } from 'react-native-gesture-handler'
import { currencyMask } from '../../../../utils/helpers/masking'
import Loading from '../../../Loading'
import CollapsableView from '@linqpal/common-frontend/src/ui/CollapsableView'
import moment from 'moment'
import LocalStorage from '../../../../store/LocalStorage'
import BuilderInvoiceSignup from '../BuilderInvoiceSignup'
import { observer } from 'mobx-react'
import {
  invoiceStatus,
  statusBadge,
} from '@linqpal/models/src/dictionaries/invoiceStatus'
import { Status } from '../../../../ui/atoms/Status'
import { useResponsive } from '../../../../utils/hooks'
import { isActiveInvoice } from './ScreenSelector'
import SignupStore from '../../../Auth/stores/SignupStore'
import SignInStore from '../../../Auth/stores/SignInStore'
import useRecaptcha from '../../../Auth/useRecaptcha'
import Toolbar from './Toolbar'
import CompanyHeader from './CompanyHeader'
import { styles } from './styles'
import { useInvoicesToPay } from '../../../../store/ScreensStore/InvoicesToPayStore'
import { CollapseIcon, EligibleForACHdiscount } from './components'

export default observer(function CompanyInvoices({
  companyInvoices,
  customer,
}) {
  const route: any = useRoute()
  const navigation: any = useNavigation()
  const [invoices, setInvoices] = useState<any[]>([])
  const [company, setCompany] = useState<any | null>(null)
  const [totalAmount, setTotalAmount] = useState(0)
  const [amountDue, setAmountDue] = useState(0)
  const [authenticate, setAuthenticate] = useState(false)
  const recaptcha = useRecaptcha('signin')
  const exists = !!customer?.existingCustomer
  const { setSelectedToPay, selectedToPay } = useInvoicesToPay()

  const { t } = useTranslation('global')

  const isUserAuthenticated = () => {
    return isWeb
      ? !!cookies.get('session')
      : RootStore.userStore.isAuthenticated
  }

  useEffect(() => {
    if (exists) {
      recaptcha.createVerifier()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exists])

  useEffect(() => {
    let activeInvoices: any[] = []
    let sum = 0
    companyInvoices.forEach((invoice) => {
      const eligible = isActiveInvoice(invoice)
      activeInvoices.push({ ...invoice, selected: eligible, eligible })
      sum += eligible ? invoice.total_amount : 0
    })

    const ids = selectedToPay?.map((i) => i._id)
    if (selectedToPay?.length) {
      const res = companyInvoices.filter(
        (i) => ids.includes(i._id) && isActiveInvoice(i),
      )
      setSelectedToPay(res)
    }

    if (sum === 0) {
      setInvoices(activeInvoices)
    } else {
      activeInvoices = activeInvoices.filter((i) => i.eligible)
      setInvoices(activeInvoices)
    }

    setCompany(activeInvoices[0].company)
    setTotalAmount(sum)
    setAmountDue(sum)
  }, [companyInvoices, setSelectedToPay, selectedToPay])

  if (!company) return <Loading />

  const onSelect = (invoiceId) => {
    const invs = invoices.map((i) => {
      if (i._id === invoiceId) return { ...i, selected: !i.selected }
      else return i
    })
    const total = invs.reduce(
      (sum, i) => (!i.selected ? sum : sum + i.total_amount),
      0,
    )
    setTotalAmount(total)
    setInvoices(invs)
  }

  const navigateToPay = () => {
    if (isUserAuthenticated() && totalAmount > 0) {
      const inv = invoices.filter((i) => i.selected)
      if (inv.length === 1) {
        navigation.navigate(paths.Console.InvoicesToPay.Invoice, {
          id: inv[0]._id,
        })
      } else {
        LocalStorage.set('companyInvoices', {
          company,
          customer,
        })
        setSelectedToPay(inv)
        navigation.navigate(paths.Console.InvoicesToPay.Pay)
      }
    }
  }

  const onPay = () => {
    if (isUserAuthenticated()) {
      navigateToPay()
    } else {
      if (!exists) {
        SignupStore.setUpInvoiceLinkSignup(customer)
      }
      SignInStore.setLogin(customer?.login)
      if (SignInStore.isValidPhone && !isUserAuthenticated()) {
        SignInStore.removeError('verify')
        SignInStore.signIn(recaptcha.verifier)
      } else if (SignInStore.isValidEmail && !isUserAuthenticated()) {
        SignInStore.setCanAskPassword(true)
      }
      setAuthenticate(true)
    }
  }

  let selectedInvoice
  let otherInvoices
  if (route.params?.id) {
    const invoiceId = route.params.id
    selectedInvoice = invoices.find((i) => i._id === invoiceId)
    otherInvoices = invoices.filter((i) => i._id !== invoiceId)
  } else {
    otherInvoices = invoices
  }

  return (
    <Wrapper
      toolbar={<Toolbar company={company} navigation={navigation} />}
      footer={<Footer totalAmount={totalAmount} t={t} onPress={onPay} />}
    >
      <CompanyHeader
        company={company}
        totalAmount={amountDue}
        style={styles.invoicesListHeader}
      />
      {selectedInvoice ? (
        <>
          <BtText size={16} weight={'600'}>
            {t('TabInvoice.NewInvoice')}
          </BtText>
          <InvoiceItem
            invoice={selectedInvoice}
            t={t}
            key={selectedInvoice._id}
            navigation={navigation}
            onSelect={onSelect}
          />
        </>
      ) : null}
      <BtText
        size={16}
        weight={'600'}
        style={{ width: '100%', textAlign: 'center', marginVertical: 14 }}
      >
        {t('TabInvoice.allCompanyInvoices', {
          companyName: company.name,
          count: invoices.length,
        })}
      </BtText>

      {otherInvoices.map((invoice) => (
        <InvoiceItem
          invoice={invoice}
          t={t}
          key={invoice._id}
          navigation={navigation}
          onSelect={onSelect}
        />
      ))}

      <BuilderInvoiceSignup
        invoice={customer}
        visible={
          authenticate &&
          (SignInStore.showVerifyPage ||
            SignupStore.isValidLogin ||
            (!exists && (customer?.phone || customer?.email)))
        }
        onClose={() => {
          setAuthenticate(false)
          navigateToPay()
        }}
        navigation={navigation}
        route={route}
      />
    </Wrapper>
  )
})

function InvoiceItem({ invoice, t, navigation, onSelect }) {
  const [collapsed, setCollapsed] = useState(true)
  return (
    <View
      style={{
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#E6EBEE',
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: 10,
        }}
      >
        {invoice.eligible && (
          <BtCheckBox
            checked={invoice.selected}
            onPress={() => onSelect(invoice._id)}
          />
        )}
        <BtText
          style={{ flex: 1, marginLeft: 8 }}
          numberOfLines={1}
          ellipsizeMode="tail"
          size={16}
        >
          #{invoice.invoice_number}
        </BtText>
        <BtText style={{ marginLeft: 8 }} weight={'700'} size={16}>
          {currencyMask(invoice.total_amount)}
        </BtText>
        <CollapseIcon
          collapsed={collapsed}
          onPress={() => setCollapsed(!collapsed)}
        />
      </View>
      {!!invoice.achDiscount && (
        <EligibleForACHdiscount
          label={t('InvoiceDetails.InvoiceEligibleForACHdiscount')}
          compactDesign
        />
      )}
      <CollapsableView collapsed={collapsed}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 5,
          }}
        >
          <View style={{ flexDirection: 'row' }}>
            <BtText style={{ color: '#99ADBA' }} weight={'600'}>
              {t('TabInvoice.due_date')}
            </BtText>
            <BtText weight={'500'} style={{ marginLeft: 5 }}>
              {t('TabInvoice.DueOn')}
            </BtText>
            <BtText weight={'500'} style={{ marginLeft: 5 }}>
              {moment(invoice.invoice_due_date).format('MMM DD')}
            </BtText>
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {invoice.status === invoiceStatus.pastDue ? (
              <Status
                style={{ marginRight: 5 }}
                label={statusBadge[invoice.status]?.label}
                textColor={statusBadge[invoice.status]?.textColor}
              />
            ) : null}

            {invoice.invoice_document ? (
              <GetIcon
                icon={<NewAttchmentIcon height="20" width="20" />}
                onPress={() => {
                  navigation.navigate(
                    paths.Console.InvoicesToPay.AttachmentPreview,
                    {
                      id: invoice._id,
                    },
                  )
                }}
              />
            ) : (
              <TouchableOpacity
                onPress={() => {
                  navigation.navigate(paths.Console.InvoicesToPay.Invoice, {
                    id: invoice._id,
                  })
                }}
              >
                <IconArrowRightLightly
                  height={20}
                  width={30}
                  style={{ width: 40 }}
                />
              </TouchableOpacity>
            )}
          </View>
        </View>
      </CollapsableView>
    </View>
  )
}

function Footer({ totalAmount, t, onPress }) {
  const { mobileWidth } = useResponsive()
  return (
    <View
      style={{
        flexDirection: 'row',
        padding: 20,
        width: mobileWidth,
        justifyContent: 'space-between',
        borderTopColor: '#E6EBEE',
        borderTopWidth: 1,
      }}
    >
      <View>
        <BtText
          weight={'500'}
          size={14}
          style={{ color: '#668598', marginBottom: 5 }}
        >
          {t('TabInvoice.TotalAmount')}
        </BtText>
        <BtText weight={'600'} size={18}>
          {currencyMask(totalAmount)}
        </BtText>
      </View>
      <BtButton
        size={'large'}
        style={{ width: 180 }}
        disabled={totalAmount === 0}
        onPress={onPress}
        testID="payTotalButton"
      >
        {t('TabInvoice.PayTotal')}
      </BtButton>
    </View>
  )
}
